/** DEMO ONLY — View 1: define the global quantity bands. */
import React, { useState, useEffect } from "react";
import { Typography, Paper, Box, Button, Alert, Snackbar } from "@mui/material";
import BandSetEditor from "../BandSetEditor";
import { validateBandSet } from "../bandValidation";
import { useDemo } from "../demoStore";
import { DEFAULT_GLOBAL_BANDS, SPLIT_GLOBAL_BANDS } from "../demoData";

export default function DefineBandsView() {
  const { state, dispatch } = useDemo();
  const [draft, setDraft] = useState(state.globalBands);
  const [toast, setToast] = useState("");

  useEffect(() => {
    setDraft(state.globalBands);
  }, [state.globalBands]);

  const errors = validateBandSet(draft, { requireContiguous: true });
  const dirty = JSON.stringify(draft) !== JSON.stringify(state.globalBands);

  const apply = () => {
    dispatch({ type: "SET_GLOBAL_BANDS", bands: draft });
    setToast("Global bands updated (demo only).");
  };

  const isSplit =
    JSON.stringify(state.globalBands) === JSON.stringify(SPLIT_GLOBAL_BANDS);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Global quantity bands
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        These bands apply to every price book entry unless an item overrides them.
        The highest band is open-ended. The global set cannot have gaps.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <BandSetEditor
          bands={draft}
          onChange={setDraft}
          requireContiguous
        />

        <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            color="success"
            onClick={apply}
            disabled={!dirty || errors.length > 0}
          >
            Apply bands
          </Button>
          <Button onClick={() => setDraft(state.globalBands)} disabled={!dirty}>
            Discard changes
          </Button>
          <Button
            onClick={() => setDraft(DEFAULT_GLOBAL_BANDS)}
            sx={{ ml: "auto" }}
          >
            Load starting 5-band set
          </Button>
        </Box>
      </Paper>

      <Alert severity="info" sx={{ mt: 2 }}>
        <strong>2026 price list scenario:</strong> the new list splits{" "}
        <code>30-99</code> into <code>30-49</code> and <code>50-99</code>.
        <Button
          size="small"
          sx={{ ml: 1 }}
          disabled={isSplit}
          onClick={() => {
            setDraft(SPLIT_GLOBAL_BANDS);
            dispatch({ type: "APPLY_SPLIT" });
            setToast(
              "Split applied. Products on the global set now need two prices for 30-49 and 50-99 — see the grid."
            );
          }}
        >
          {isSplit ? "Split applied" : "Apply the 30-49 / 50-99 split"}
        </Button>
      </Alert>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast("")}
        message={toast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}
